import { Empty, FormattedDateTime, Loading } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { connectedAccountsService } from "../shared/api/connectedAccountsService";

const EmptyContainer = styled.div`
	padding: ${({ theme }) => theme.spacing(36)} 0;
`;

const List = styled.ul`
	margin-top: ${({ theme }) => theme.spacing(6)};
	list-style: none;

	> * + * {
		border-top: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
	}
`;

const ListItem = styled.li`
	display: grid;
	grid-template-columns: 1fr 1fr max-content;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
	padding: ${({ theme }) => theme.spacing(4)} ${({ theme }) => theme.spacing(2)};
`;

const Text = styled.span`
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	color: ${({ theme }) => theme.colors.palette.grey[60]};
`;

const Centered = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${({ theme }) => theme.spacing(36)} 0;
`;

const TableHeading = styled.span`
	${({ theme }) => theme.fonts.weights.bold};
`;

export const ConnectedAccounts: FC = () => {
	const { t } = useTranslation();
	const { data: connectedAccounts, isLoading } = useQuery({
		queryKey: connectedAccountsService.baseKey,
		queryFn: () => connectedAccountsService.all().then((r) => r.data.data),
	});

	if (!connectedAccounts || connectedAccounts.length === 0) {
		return isLoading ? (
			<Centered>
				<Loading />
			</Centered>
		) : (
			<EmptyContainer>
				<Empty>{t("No connected accounts available.")}</Empty>
			</EmptyContainer>
		);
	}

	return (
		<List>
			<ListItem>
				<TableHeading>{t("External account id")}</TableHeading>
				<TableHeading>{t("Provider")}</TableHeading>
				<TableHeading>{t("Connected at")}</TableHeading>
			</ListItem>
			{connectedAccounts.map((account) => (
				<ListItem key={account.socialiteId}>
					<Text>{account.socialiteId}</Text>
					<Text>{account.provider}</Text>
					<Text>
						<FormattedDateTime dateTime={account.createdAt} />
					</Text>
				</ListItem>
			))}
		</List>
	);
};
